'use client';
import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';

import { cn } from '@skybeam/shared-utils/classnames/cn';
import { Button } from '@skybeam/shared-ui-kit/ui/button';
import { NavbarMobile } from './navbar-mobile';
import { NavItem } from './nav-item';
import { ReactComponent as Logo } from '../../../../public/skybeam-logo-with-text-blue.svg';
import { ReactComponent as MenuSVG } from '../../../../public/menu.svg';

import { Video } from 'lucide-react';
import { ShoppingBag } from 'lucide-react';
import { ShoppingCart } from 'lucide-react';
import { HandPlatter } from 'lucide-react';
import { House } from 'lucide-react';
import { BriefcaseBusiness } from 'lucide-react';
import { CircleSmall } from 'lucide-react';
import { ChevronDown } from 'lucide-react';

import styles from './navbar.module.css';

const navItems = [
  { link: '/insights', label: 'Insights Hub' },
  { link: '', label: 'Industries' },
  { link: '/blog', label: 'Blog' },
  { link: '/pricing', label: 'Pricing' },
  { link: '/about', label: 'About Us' },
];

const industriesSubItems = [
  {
    link: '/industry/agencies',
    label: 'Agencies',
    icon: (
      <Video className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-600" />
    ),
    featuredSrc: '/agencies-bg.jpg',
    featuredTitle: 'CTV Advertising Platform Built For Agencies',
  },
  {
    link: '/industry/franchisees',
    label: 'Franchisees',
    icon: (
      <ShoppingBag className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-900" />
    ),
    featuredSrc: '/franchises-bg.png',
    featuredTitle: 'TV Advertising Built for Franchise Growth',
  },
  {
    link: '',
    label: 'eCommerce',
    icon: (
      <ShoppingCart className="desktop:size-6 size-4 text-white group-hover:fill-blue-600 group-hover:text-blue-600" />
    ),
  },
  {
    link: '',
    label: 'Restaurants',
    icon: (
      <HandPlatter className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-600" />
    ),
  },
  {
    link: '',
    label: 'Home Services',
    icon: (
      <House className="desktop:size-6 size-4 text-white transition-all duration-300 group-hover:fill-blue-600 group-hover:text-blue-600" />
    ),
  },
  {
    link: '',
    label: "SMB's",
    icon: (
      <BriefcaseBusiness className="desktop:size-6 size-4 text-white group-hover:fill-blue-600 group-hover:text-blue-600" />
    ),
  },
];

export const Navbar: React.FC = () => {
  const pathname = usePathname();

  const dashboardUrl = process.env.NEXT_PUBLIC_DASHBOARD_URL;

  const getInitialNavbarMode = () => {
    if (pathname.startsWith('/blog/')) {
      return 'white';
    }
    if (pathname !== '/' && pathname !== '/blog' && pathname !== '/pricing') {
      return 'light';
    }
    return 'dark';
  };

  const [isNavbarMobileOpen, setIsNavbarMobileOpen] = useState(false);
  const [isNavbarExpanded, setIsNavbarExpanded] = useState(false);
  const [hoveredIndustryIndex, setHoveredIndustryIndex] = useState(0);
  const [navbarMode, setNavbarMode] = useState<'light' | 'dark' | 'white'>(
    getInitialNavbarMode,
  );

  const handleOpenMenu = () => {
    setIsNavbarMobileOpen(true);
  };

  const handleCloseMenu = () => {
    setIsNavbarMobileOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');
      let currentSection: any = null;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 80 && rect.bottom >= 80) {
          currentSection = section as HTMLElement;
        }
      });

      if (currentSection) {
        const sectionId = currentSection.getAttribute('data-section');

        if (
          sectionId === 'heading-section' ||
          sectionId === 'faq-section' ||
          sectionId === 'resources-section' ||
          sectionId === 'pricing-heading-section'
        ) {
          setNavbarMode('dark');
        } else if (sectionId === 'article-section') {
          setNavbarMode('white');
        } else {
          setNavbarMode('light');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [pathname]);

  if (isNavbarMobileOpen) {
    return (
      <NavbarMobile
        navItems={navItems}
        industrySubItems={industriesSubItems}
        navbarMode={navbarMode}
        setClose={handleCloseMenu}
      />
    );
  }

  return (
    <div
      data-test-header
      className={cn(
        styles.navbar,
        styles[navbarMode],
        'tablet:py-5 relative z-[100] flex w-full justify-center border-b border-t border-transparent py-2 transition-colors duration-500',
      )}
    >
      <div className="tablet:px-6 laptop:px-10 desktop:px-0 desktop:max-w-[71rem] flex w-full max-w-full items-center justify-between px-0">
        <div className="flex">
          <MenuSVG
            className={cn(
              'tablet:mr-8 desktop:hidden mr-4',
              navbarMode === 'white' ? 'text-[#0A0A0A]' : 'text-white',
            )}
            onClick={handleOpenMenu}
          />
          <Link href="/">
            <Logo
              id="skybeam-logo"
              className={cn('tablet:size-full h-[32px] w-[99px]', styles.logo)}
            />
          </Link>
          <div className="desktop:flex hidden items-center pl-10">
            {navItems.map(({ label, link }) => {
              if (link) {
                return (
                  <NavItem
                    key={link}
                    href={link}
                    className="px-4 py-1"
                    isActive={pathname === link && pathname !== '/'}
                  >
                    {label}
                  </NavItem>
                );
              }
              return (
                <div
                  key={label}
                  className="font-proximaNova"
                  onMouseEnter={() => setIsNavbarExpanded(true)}
                >
                  <div className="flex items-center gap-1 px-4 py-1 text-base">
                    {label}
                    <ChevronDown className={cn(styles.arrowDown, 'size-4')} />
                  </div>

                  {isNavbarExpanded && (
                    <div
                      className={cn(
                        'absolute left-0 top-[78px] flex w-full justify-center pb-16 pt-5 transition-colors duration-500',
                        styles[navbarMode],
                      )}
                      onMouseLeave={() => setIsNavbarExpanded(false)}
                    >
                      <div className="flex w-full max-w-[71rem] items-start gap-x-24">
                        <div className="grid w-full grid-flow-col grid-rows-3 gap-x-2">
                          {industriesSubItems.map(
                            ({ label, link, icon }, index) => {
                              if (link) {
                                return (
                                  <Link
                                    key={label}
                                    className="self-start"
                                    href={link}
                                    onClick={() => setIsNavbarExpanded(false)}
                                  >
                                    <div
                                      className="group flex h-full items-center gap-x-5 rounded-sm p-4 transition-all duration-300 hover:bg-white hover:text-black"
                                      onMouseEnter={() =>
                                        setHoveredIndustryIndex(index)
                                      }
                                    >
                                      <div className="flex size-12 items-center justify-center rounded-sm border border-blue-500 bg-blue-700 group-hover:border-blue-200 group-hover:bg-blue-100">
                                        {icon}
                                      </div>
                                      <span className="text-base font-semibold">
                                        {label}
                                      </span>
                                    </div>
                                  </Link>
                                );
                              }
                              return (
                                <div
                                  key={label}
                                  className="flex items-center gap-x-5 p-4"
                                >
                                  <div className="flex size-12 items-center justify-center rounded-sm border border-blue-500 bg-blue-700">
                                    {icon}
                                  </div>
                                  <span className="text-base font-semibold">
                                    {label}
                                  </span>
                                  <div
                                    className={cn(
                                      'flex items-center gap-x-0.5 rounded-md py-0.5 pl-1 pr-2 text-sm',
                                      navbarMode === 'light'
                                        ? 'bg-blue-800'
                                        : 'bg-blue-950',
                                    )}
                                  >
                                    <CircleSmall className="size-3 fill-lime-200 text-lime-200" />
                                    <span className="text-white">Soon</span>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                        <div className="w-full max-w-[456px]">
                          {hoveredIndustryIndex !== -1 && (
                            <div>
                              <Image
                                src={
                                  industriesSubItems[hoveredIndustryIndex]
                                    .featuredSrc!
                                }
                                alt={
                                  industriesSubItems[hoveredIndustryIndex]
                                    .featuredTitle!
                                }
                                width={456}
                                height={222}
                                className="h-[222px] max-h-[222px] max-w-[456px] rounded-lg object-cover"
                              />
                              <div
                                className={cn(
                                  navbarMode === 'light'
                                    ? 'bg-white text-blue-700'
                                    : 'bg-blue-700 text-white',
                                  '-mt-[14px] rounded-xl px-5 pb-5 pt-8',
                                )}
                              >
                                <Link
                                  href={
                                    industriesSubItems[hoveredIndustryIndex]
                                      .link
                                  }
                                  onClick={() => setIsNavbarExpanded(false)}
                                >
                                  {
                                    industriesSubItems[hoveredIndustryIndex]
                                      .featuredTitle
                                  }
                                </Link>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="tablet:flex hidden items-center gap-x-2">
          <NavItem
            href={`${process.env.NEXT_PUBLIC_HUBSPOT_URL}`}
            className={styles.navButton}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              data-test="Book a Demo-btn"
              className={cn(
                'body-medium h-[38px] hover:no-underline',
                navbarMode !== 'white' ? 'text-white' : 'text-zinc-950',
              )}
              variant="link"
            >
              Book a Demo
            </Button>
          </NavItem>
          <Link href={`${dashboardUrl}/sign-up`}>
            <Button
              data-test="Start Now-btn"
              className={cn(
                'body-medium h-[38px] rounded-lg',
                navbarMode === 'dark'
                  ? 'marketing-primary-button'
                  : 'marketing-tertiary-button',
              )}
              variant="secondary"
            >
              Start Now
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
